import { Component, Inject, OnInit, OnDestroy, SecurityContext } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { LoaderService } from '../../services/loader.service';

export interface FilePreviewData {
  fileUrl: string;
  filename: string;
  mimeType: string;
  fileSize: number;
  isImage: boolean;
  isPdf: boolean;
  isDocument: boolean;
  isWord: boolean;
  isExcel: boolean;
  isPowerPoint: boolean;
}

@Component({
  selector: 'app-custom-file-preview-dialog',
  templateUrl: './custom-file-preview-dialog.component.html',
  styleUrls: ['./custom-file-preview-dialog.component.scss']
})
export class CustomFilePreviewDialogComponent implements OnInit, OnDestroy {
  
  imageUrl: SafeResourceUrl | null = null;
  microsoftViewerUrl: SafeResourceUrl | null = null;
  loading = true;
  error = false;
  errorMessage = '';
  documentError = false;
  microsoftViewerLoading = false;
  loadingTime = 0;
  pdfLoading = false;
  pdfLoadingTime = 0;
  private loadingTimeout: any;
  private loadingStartTime: number = 0;
  private loadingTimer: any;
  private pdfLoadingStartTime: number = 0;
  private pdfLoadingTimer: any;


  constructor(
    public dialogRef: MatDialogRef<CustomFilePreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilePreviewData,
    private sanitizer: DomSanitizer,
    private loader: LoaderService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.loadFilePreview();
    
    // Set a timeout fallback to hide loader after 90 seconds
    // this.loadingTimeout = setTimeout(() => {
    //   if (this.loading) {
    //     console.warn('Loading timeout reached, hiding loader');
    //     this.loading = false;
    //   }
    // }, 90000);
  }

  ngOnDestroy(): void {
    // Clean up timeouts and timers
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    if (this.loadingTimer) {
      clearInterval(this.loadingTimer);
    }
    if (this.pdfLoadingTimer) {
      clearInterval(this.pdfLoadingTimer);
    }
  }

  private loadFilePreview(): void {
    try {
      if (this.data.isImage) {
        // For images, display them directly but keep loading until image loads
        this.imageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.data.fileUrl);
        console.log('Image load Started');
        // Don't set loading = false here, wait for onImageLoad()
      } else if (this.data.isWord || this.data.isExcel || this.data.isPowerPoint || this.isExcelFile() || this.isPowerPointFile()) {
        // For Word, Excel, and PowerPoint documents, generate Microsoft Viewer URL once
        this.generateMicrosoftViewerUrl();
      } else if (this.data.isPdf) {
        // For PDF files, use PDF.js viewer
        console.log('🔴 Loading PDF with PDF.js viewer');
        this.startPdfLoading();
        this.loading = false;
      } else {
        // For other documents, use ngx-doc-viewer which handles loading internally
        // Don't set loading = false here, wait for onDocumentLoaded()
      }
    } catch (error) {
      console.error('Error loading file preview:', error);
      this.error = true;
      this.errorMessage = 'Failed to load file preview';
      this.loading = false;
      this.clearLoadingTimeout();
    }
  }

  onDocumentLoaded(event: any): void {
    console.log('Document loaded successfully');
    // For non-Word documents with Google Docs viewer, wait longer for content rendering
    setTimeout(() => {
      this.loading = false;
      this.clearLoadingTimeout();
    }, 5000);
  }

  onDocumentError(event: any): void {
    console.warn('Document failed to load:', event);
    this.documentError = true;
    this.loading = false;
    this.clearLoadingTimeout();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onDownload(): void {
    // Trigger download by opening the URL in a new tab
    window.open(this.data.fileUrl, '_blank');
  }







  onImageLoad(): void {
    console.log('Image loaded successfully');
    this.loading = false;
    this.clearLoadingTimeout();
  }

  onImageError(): void {
    console.log('Image failed to load');
    this.error = true;
    this.errorMessage = 'Failed to load image';
    this.loading = false;
    this.clearLoadingTimeout();
  }

  private clearLoadingTimeout(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }



  getFileIcon(): string {
    if (this.data.isPdf) return 'picture_as_pdf';
    if (this.data.isImage) return 'image';
    if (this.data.isWord) return 'description';
    if (this.data.isExcel) return 'table_chart';
    if (this.data.isPowerPoint) return 'slideshow';
    if (this.data.mimeType?.includes('video')) return 'video_file';
    return 'insert_drive_file';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private generateMicrosoftViewerUrl(): void {
    // Check if fileUrl is a blob URL - Microsoft Viewer can't access blob URLs
    if (this.data.fileUrl.startsWith('blob:') || this.data.fileUrl.startsWith('data:')) {
      console.warn('⚠️ Blob/Data URL detected - Microsoft Viewer cannot access blob/data URLs. Showing download option.');
      this.error = true;
      this.errorMessage = 'Preview not available for this file type. Please download the file to view it.';
      this.loading = false;
      this.clearLoadingTimeout();
      return;
    }
    
    const encodedUrl = encodeURIComponent(this.data.fileUrl);
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
    const documentType = this.getDocumentType();
    console.log(`🚀 Microsoft Viewer URL generated for ${documentType} document:`, viewerUrl);
    this.microsoftViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    
    // Start Microsoft Viewer loading state and timing
    this.startMicrosoftViewerTiming();
    this.loading = false;
  }

  private startMicrosoftViewerTiming(): void {
    this.microsoftViewerLoading = true;
    this.loadingStartTime = Date.now();
    this.loadingTime = 0;
    
    const documentType = this.getDocumentType();
    console.log(`⏱️ Microsoft Viewer loading started for ${documentType} document at:`, new Date().toISOString());
    console.log('📁 File details:', {
      filename: this.data.filename,
      fileSize: this.formatFileSize(this.data.fileSize),
      mimeType: this.data.mimeType,
      fileUrl: this.data.fileUrl
    });
    
    // Update loading time every second
    this.loadingTimer = setInterval(() => {
      this.loadingTime = Math.floor((Date.now() - this.loadingStartTime) / 1000);
      
      // Log every 10 seconds for debugging
      if (this.loadingTime % 10 === 0 && this.loadingTime > 0) {
        console.log(`⏳ Still loading Microsoft Viewer: ${this.loadingTime}s elapsed`);
      }
      
      // Failsafe: Force stop loading after 45 seconds
      if (this.loadingTime >= 45) {
        console.warn('🚨 Microsoft Viewer taking too long (45s+), forcing completion');
        this.stopMicrosoftViewerTiming();
      }
    }, 1000);
  }

  private stopMicrosoftViewerTiming(): void {
    if (this.loadingTimer) {
      clearInterval(this.loadingTimer);
      this.loadingTimer = null;
    }
    
    const totalTime = Math.floor((Date.now() - this.loadingStartTime) / 1000);
    console.log('✅ Microsoft Viewer loading completed in:', totalTime + 's');
    console.log('📊 Loading performance:', {
      document: this.data.filename,
      fileSize: this.formatFileSize(this.data.fileSize),
      loadingTime: totalTime + 's',
      timestamp: new Date().toISOString()
    });
    
    this.microsoftViewerLoading = false;
  }

  onMicrosoftViewerLoaded(): void {
    const currentTime = Math.floor((Date.now() - this.loadingStartTime) / 1000);
    console.log(`📄 Microsoft Viewer iframe loaded after ${currentTime}s`);
    this.loading = false;
    this.clearLoadingTimeout();
    
    // Give Microsoft Viewer some time to load content before hiding overlay
    setTimeout(() => {
      if (this.microsoftViewerLoading) {
        const timeAtHide = Math.floor((Date.now() - this.loadingStartTime) / 1000);
        console.log(`⏰ Auto-hiding Microsoft Viewer loading overlay after iframe load (${timeAtHide}s total)`);
        this.stopMicrosoftViewerTiming();
      }
    }, 3000); // Reduced to 3 seconds after iframe loads
  }

  onMicrosoftViewerError(event: any): void {
    const currentTime = Math.floor((Date.now() - this.loadingStartTime) / 1000);
    console.error(`❌ Microsoft Viewer iframe error after ${currentTime}s:`, event);
    console.log('📊 Error performance:', {
      document: this.data.filename,
      fileSize: this.formatFileSize(this.data.fileSize),
      loadingTime: currentTime + 's (error occurred)',
      error: event,
      timestamp: new Date().toISOString()
    });
    
    this.stopMicrosoftViewerTiming();
    // Could show error message or fallback to download
  }

  forceStopLoading(): void {
    const totalTime = Math.floor((Date.now() - this.loadingStartTime) / 1000);
    console.warn(`🛑 User manually stopped loading after ${totalTime}s`);
    console.log('📊 Forced stop performance:', {
      document: this.data.filename,
      fileSize: this.formatFileSize(this.data.fileSize),
      loadingTime: totalTime + 's (manually stopped)',
      timestamp: new Date().toISOString()
    });
    
    this.stopMicrosoftViewerTiming();
    this.onDownload(); // Automatically trigger download
  }

  getDocumentType(): string {
    if (this.data.isWord) return 'Word';
    if (this.data.isExcel || this.isExcelFile()) return 'Excel';
    if (this.data.isPowerPoint || this.isPowerPointFile()) return 'PowerPoint';
    return 'Office';
  }

  isExcelFile(): boolean {
    if (!this.data.filename) return false;
    const filename = this.data.filename.toLowerCase();
    return filename.endsWith('.xls') || filename.endsWith('.xlsx');
  }

  isPowerPointFile(): boolean {
    if (!this.data.filename) return false;
    const filename = this.data.filename.toLowerCase();
    return filename.endsWith('.ppt') || filename.endsWith('.pptx');
  }

  isOfficeDocument(): boolean {
    return this.data.isWord || this.data.isExcel || this.data.isPowerPoint || 
           this.isExcelFile() || this.isPowerPointFile();
  }

  // PDF.js Methods
  private startPdfLoading(): void {
    this.pdfLoading = true;
    this.pdfLoadingStartTime = Date.now();
    this.pdfLoadingTime = 0;
    
    console.log('⏱️ PDF.js loading started at:', new Date().toISOString());
    console.log('📁 PDF details:', {
      filename: this.data.filename,
      fileSize: this.formatFileSize(this.data.fileSize),
      mimeType: this.data.mimeType,
      fileUrl: this.data.fileUrl
    });
    
    // Update loading time every second
    this.pdfLoadingTimer = setInterval(() => {
      this.pdfLoadingTime = Math.floor((Date.now() - this.pdfLoadingStartTime) / 1000);
      
      // Log every 10 seconds for debugging
      if (this.pdfLoadingTime % 10 === 0 && this.pdfLoadingTime > 0) {
        console.log(`⏳ Still loading PDF: ${this.pdfLoadingTime}s elapsed`);
      }
      
      // Failsafe: Force stop loading after 30 seconds
      if (this.pdfLoadingTime >= 30) {
        console.warn('🚨 PDF taking too long (30s+), forcing completion');
        this.stopPdfLoading();
      }
    }, 1000);
  }

  private stopPdfLoading(): void {
    if (this.pdfLoadingTimer) {
      clearInterval(this.pdfLoadingTimer);
      this.pdfLoadingTimer = null;
    }
    
    const totalTime = Math.floor((Date.now() - this.pdfLoadingStartTime) / 1000);
    console.log('✅ PDF.js loading completed in:', totalTime + 's');
    console.log('📊 PDF Loading performance:', {
      document: this.data.filename,
      fileSize: this.formatFileSize(this.data.fileSize),
      loadingTime: totalTime + 's',
      timestamp: new Date().toISOString()
    });
    
    this.pdfLoading = false;
  }

  onPdfLoaded(event: any): void {
    console.log('📄 PDF loaded successfully:', event);
    this.stopPdfLoading();
  }

  onPdfError(event: any): void {
    console.error('❌ PDF loading failed:', event);
    this.stopPdfLoading();
    this.documentError = true;
  }

  onPdfPageRendered(event: any): void {
    console.log('📖 PDF page rendered:', event.pageNumber);
  }
}
